import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ShowRecallCampaignsSettings } from './settings/recall-campaigns-settings';
import { ShowRecallCampaignsUsers } from './settings/recall-campaigns-users';
import { DomSanitizer } from '@angular/platform-browser';
import { BotConfigService } from '../../../../services/bot-config.service';
import { UploadCampaignService, recallCampaign } from '../../../../services/upload.campaign.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { ActivatedRoute, Router } from '@angular/router';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { ConfirmationModalComponent } from 'src/app/modules/shared/confirmation-modal/confirmation-modal.component';
import { ComponentWithDestroy } from 'src/app/modules/shared/component-with-destroy';
import { take, takeUntil } from 'rxjs/operators';

const ITEMS_PER_PAGE = 5;

@Component({
  selector: 'app-bot-upload-campaigns',
  templateUrl: './bot-upload-campaigns.component.html',
  styleUrls: ['./bot-upload-campaigns.component.scss'],
})
export class BotUploadCampaignsComponent extends ComponentWithDestroy implements OnInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  public isLoading = true;

  public currentStep: string;
  private botConfig: any;
  public recallCampaigns: MatTableDataSource<any>;
  @Input() userEmail: string;

  @Input() userName: string;

  @Input() botId: string;

  displayedColumns = ['createdAt', 'userName', 'startDate', 'maxBatch', 'fileName', 'customersCount', 'numberOfUsers', 'cancelProcessing'];

  public cancellingFileIds = new Set<string>();

  constructor(
    private domSanitizer: DomSanitizer,
    private botConfigService: BotConfigService,
    private uploadCampaign: UploadCampaignService,
    private router: Router,
    private route: ActivatedRoute,
    private modalService: BsModalService,
    private toastr: ToastrService,
  ) {
    super();
    this.currentStep = 'SHOW_RECALL_CAMPAIGNS';
  }

  public showRecallCampaignsSettings: any;
  public showRecallCampaignUsers: any;
  public dealershipId = '';
  async ngOnInit() {
    this.showRecallCampaignsSettings = new ShowRecallCampaignsSettings(this.domSanitizer).get(ITEMS_PER_PAGE);
    this.showRecallCampaignUsers = new ShowRecallCampaignsUsers(this.domSanitizer).get();
    this.botConfig = await this.botConfigService.getBotConfigById(this.botId);
    this.dealershipId = this.botConfig.code;

    const campaignsFromDataSource = await this.uploadCampaign.getConversations(this.dealershipId);
    for (let x = 0; x < campaignsFromDataSource.length % ITEMS_PER_PAGE; x += 1) {
      campaignsFromDataSource.push({
        customersCount: 0,
        dealershipCode: '',
        fileId: '',
        fileName: '',
        id: '',
        maxBatch: 0,
        startDate: '',
        userName: '',
      });
    }
    this.recallCampaigns = new MatTableDataSource<any>(campaignsFromDataSource);
    setTimeout(() => {
      this.recallCampaigns.paginator = this.paginator;
      this.recallCampaigns.sort = this.sort;
    }, 100);
    this.isLoading = false;
  }
  addNewCampaign(e): any {
    this.router.navigate(['create-new-campaign'], {
      relativeTo: this.route,
      state: {
        dealershipId: this.dealershipId,
        userName: this.userName,
      },
    });
  }

  public canCancelProcessing(campaign: recallCampaign): boolean {
    return !!campaign.fileName && !this.cancellingFileIds.has(campaign.fileId);
  }

  public cancelButtonLabel(campaign: recallCampaign): string {
    return this.cancellingFileIds.has(campaign.fileId) ? 'Cancelling…' : 'Cancel Processing';
  }

  public async confirmCancelProcessing(campaign: recallCampaign): Promise<void> {
    const manifestIds = campaign.manifestIds ?? [];

    if (!manifestIds.length) {
      this.toastr.error(`${campaign.fileName} was uploaded before cancelling was available, so it can't be stopped from here. Please contact support.`);

      return;
    }

    this.cancellingFileIds.add(campaign.fileId);

    try {
      const dealerCode = campaign.dealershipCode || this.dealershipId;
      const conversationCount = await this.countConversations(dealerCode, manifestIds);

      if (!conversationCount) {
        this.toastr.info(`Nothing left to cancel for ${campaign.fileName}. Its conversations have already stopped.`);
        return;
      }

      this.promptToCancel(campaign, manifestIds, conversationCount);
    } catch (error) {
      this.toastr.error(this.failureMessage(error, campaign, false));
    } finally {
      this.cancellingFileIds.delete(campaign.fileId);
    }
  }

  /**
   * Says what happened to the list the user is looking at, and what they can do about it. The
   * distinction that matters is whether we had already started cancelling: a failure while counting
   * changed nothing, while a failure part way through may have stopped some conversations already,
   * and telling the user "nothing was cancelled" in that case would not be true.
   */
  private failureMessage(error: unknown, campaign: recallCampaign, cancelHadStarted: boolean): string {
    const status = error instanceof HttpErrorResponse ? error.status : 0;

    if (status === 400) {
      return `${campaign.fileName} is missing the reference we need to stop it, so it can't be cancelled here. Please contact support.`;
    }

    if (status === 404) {
      return `We couldn't find a campaign for ${campaign.fileName}. It may have already finished, so there is nothing to cancel.`;
    }

    return cancelHadStarted
      ? `Something went wrong while cancelling ${campaign.fileName}. Some conversations may still be running, so please try again.`
      : `We couldn't check ${campaign.fileName} just now. Nothing was cancelled, so please try again.`;
  }

  /** Asks the cancel API what it would terminate, without terminating anything. */
  private async countConversations(dealerCode: string, manifestIds: string[]): Promise<number> {
    const counts = await Promise.all(manifestIds.map(manifestId => this.uploadCampaign.killManifest(dealerCode, manifestId, true)));

    return counts.reduce((total, count) => total + count, 0);
  }

  private promptToCancel(campaign: recallCampaign, manifestIds: string[], conversationCount: number): void {
    const modalRef = this.modalService.show(ConfirmationModalComponent, { ignoreBackdropClick: true });

    modalRef.content.title = 'Cancel Processing';
    modalRef.content.message = `This will stop ${conversationCount} ${conversationCount === 1 ? 'conversation' : 'conversations'} for ${
      campaign.fileName
    }. This can't be undone.`;

    // Only a confirmation triggers the cancel; dismissing the prompt takes no action at all.
    modalRef.content.confirm.pipe(take(1), takeUntil(this.destroy$)).subscribe(() => {
      this.cancelProcessing(campaign, manifestIds);
    });
  }

  private async cancelProcessing(campaign: recallCampaign, manifestIds: string[]): Promise<void> {
    const dealerCode = campaign.dealershipCode || this.dealershipId;

    this.cancellingFileIds.add(campaign.fileId);

    try {
      const counts = await Promise.all(manifestIds.map(manifestId => this.uploadCampaign.killManifest(dealerCode, manifestId, false)));
      const terminated = counts.reduce((total, count) => total + count, 0);

      this.toastr.success(`Cancelled ${terminated} ${terminated === 1 ? 'conversation' : 'conversations'} for ${campaign.fileName}.`);
    } catch (error) {
      this.toastr.error(this.failureMessage(error, campaign, true));
    } finally {
      this.cancellingFileIds.delete(campaign.fileId);
    }
  }
}
